import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import type {
    CreateContactInput,
    CreateContactMutation,
    CreateContactMutationVariables,
    UpdateContactInput,
    UpdateContactMutation,
    UpdateContactMutationVariables,
    DeleteContactMutation,
    DeleteContactMutationVariables,
} from '@/graphql/types';
import {
    CreateContactDocument,
    UpdateContactDocument,
    DeleteContactDocument,
} from '@/graphql/types';
import { useContactMutationExecutor } from './useContactMutationExecutor';

export function useContactMutations() {
    const { executeMutation } = useContactMutationExecutor();

    const [createMutation, { loading: creating }] = useMutation<
        CreateContactMutation,
        CreateContactMutationVariables
    >(CreateContactDocument);

    const [updateMutation, { loading: updating }] = useMutation<
        UpdateContactMutation,
        UpdateContactMutationVariables
    >(UpdateContactDocument);

    const [deleteMutation, { loading: deleting }] = useMutation<
        DeleteContactMutation,
        DeleteContactMutationVariables
    >(DeleteContactDocument);

    const create = useCallback(
        (payload: CreateContactInput) =>
            executeMutation(createMutation, { input: payload }, 'createContact'),
        [executeMutation, createMutation]
    );

    const update = useCallback(
        (payload: UpdateContactInput) =>
            executeMutation(updateMutation, { input: payload }, 'updateContact'),
        [executeMutation, updateMutation]
    );

    const remove = useCallback(
        (id: string) => executeMutation(deleteMutation, { input: { id } }, 'deleteContact'),
        [executeMutation, deleteMutation]
    );

    return {
        create,
        update,
        remove,
        isMutating: creating || updating,
        isDeleting: deleting,
    };
}
